<template>
    <div class="zs-list-container">
        <div v-if="typeof props.filter === 'function' || props.filter" class="zs-list-filter">
            <ZSInput type="text" v-model="filterText" placeholder="Filter items..." />
        </div>
        <ul class="zs-list" v-bind="$attrs">
            <li v-for="(item, index) in filteredItems" :key="item[itemKey]" :class="{
                'zs-list-item': true,
                'selected': isSelected(item),
                'disabled': isItemDisabled(item, index),
                'selectable': selectable && !multiselect
            }" @click="handleItemClick(item, index)">
                <ZSCheckBox label="" v-if="multiselect" :checked="isSelected(item)"
                    :disabled="isItemDisabled(item, index)" @change="handleCheckboxChange(item, $event, index)"
                    @click.stop />
                <slot :item="item" :isSelected="isSelected(item)" :isDisabled="isItemDisabled(item, index)">
                    {{ getItemDisplay(item) }}
                </slot>
            </li>
        </ul>
        <div v-if="!filteredItems.length" class="zs-list-empty">
            {{ emptyText }}
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import ZSCheckBox from '../form/ZSCheckBox.vue';
import ZSInput from '../form/ZSInput.vue';


const props = defineProps({
    items: {
        type: Array,
        required: true,
        default: () => []
    },
    itemKey: {
        type: String,
        required: true
    },
    itemLabel: {
        type: [String, Function],
        default: null
    },
    selectable: {
        type: Boolean,
        default: true
    },
    multiselect: {
        type: Boolean,
        default: false
    },
    filter: {
        type: [Boolean, Function], // Allow Boolean or Function
        default: false
    },
    // Allow v-model for single selection
    selected: {
        type: Object,
        default: null
    },
    // Allow v-model for multiple selection
    multiSelected: {
        type: Array,
        default: () => []
    },
    emptyText: {
        type: String,
        default: 'No items to display'
    },
    disable: { // New prop for disabling items
        type: [String, Number, Array, Function],
        default: null
    }
});

const emit = defineEmits(['update:selected', 'update:multiSelected', 'selection-change']);

const filterText = ref('');
const internalSelected = ref(props.selected);
const internalMultiSelected = ref([...props.multiSelected]); // Create a reactive copy

// Sync internal state with props (for v-model)
watch(() => props.selected, (newVal) => {
    if (!props.multiselect) {
        internalSelected.value = newVal;
    }
});

watch(() => props.multiSelected, (newVal) => {
    if (props.multiselect) {
        internalMultiSelected.value = [...newVal];
    }
}, { deep: true });


const isItemDisabled = (item, index) => {
    if (props?.disable !== 0 && !props.disable && typeof props.disable !== 'boolean') { // Adjusted condition to allow boolean false
        return false;
    }
    const disableType = typeof props.disable;

    if (disableType === 'boolean') { // Handle boolean type
        return props.disable; // If true, all items are disabled; if false, none are (based on this prop)
    } else if (disableType === 'string') {
        return !!item[props.disable]; // Check boolean value of the specified field
    } else if (disableType === 'number') {
        return index === props.disable; // Check against single index
    } else if (Array.isArray(props.disable)) {
        return props.disable.includes(index); // Check if index is in the array
    } else if (disableType === 'function') {
        return props.disable(item, index); // Execute the provided function
    }
    return false;
};


const filteredItems = computed(() => {
    const filterType = typeof props.filter;

    if (filterType === 'function') {
        // Use the provided function for filtering
        return props.items.filter((item) => {
            return filterText.value.length === 0 || props.filter(filterText.value, item);
        });
    } else if (filterType === 'boolean' && props.filter && filterText.value) {
        // Use the internal text filter only if filter is true and text is entered
        const lowerFilter = filterText.value.toLowerCase();
        return props.items.filter(item => {
            const display = getItemDisplay(item).toLowerCase();
            return display.includes(lowerFilter);
        });
    } else {
        // No filtering or filter is boolean false
        return props.items;
    }
});

const isSelected = (item) => {
    if (props.multiselect) {
        return internalMultiSelected.value.some(selectedItem => selectedItem[props.itemKey] === item[props.itemKey]);
    } else if (props.selectable) {
        return internalSelected.value && internalSelected.value[props.itemKey] === item[props.itemKey];
    }
    return false;
};

const handleItemClick = (item, index) => { // Add index parameter
    if (isItemDisabled(item, index)) return; // Prevent action if disabled

    if (props.multiselect) {
        // Toggle selection for multiselect
        toggleMultiSelection(item);
    } else if (props.selectable) {
        // Set selection for single select

        internalSelected.value = item;
        emit('update:selected', item);
        emit('selection-change', item);
    }
};

const handleCheckboxChange = (item, event, index) => { // Add index parameter
    if (isItemDisabled(item, index)) return; // Prevent action if disabled
    // This is primarily handled by handleItemClick now for consistency,
    // but we ensure the state is correct based on the checkbox interaction
    // especially if the click was directly on the checkbox.
    if (props.multiselect) {
        toggleMultiSelection(item);
    }
};


const toggleMultiSelection = (item) => {
    const index = internalMultiSelected.value.findIndex(selectedItem => selectedItem[props.itemKey] === item[props.itemKey]);
    if (index > -1) {
        // Deselect
        internalMultiSelected.value.splice(index, 1);
    } else {
        // Select
        internalMultiSelected.value.push(item);
    }
    emit('update:multiSelected', [...internalMultiSelected.value]); // Emit a new array
    emit('selection-change', [...internalMultiSelected.value]);
};


const getItemDisplay = (item) => {
    if (props.itemLabel) {
        if (typeof props.itemLabel === 'function') {
            return props.itemLabel(item);
        }
        return item[props.itemLabel];
    }
    // Fallback: try common keys or stringify
    return item.label || item.name || item.text || JSON.stringify(item);
};

</script>

<style >
:root {
    /* ZSList colors */
    --color-list-border: #ccc;
    --color-list-item-border: #eee;
    --color-list-item-hover: #f5f5f5;
    --color-list-item-selected: #e0e0e0;
    --color-list-item-disabled: #f8f8f8;
    --color-list-empty-text: #777;
    --color-list-filter-border: #ddd;
    --color-blue: #4384BD;
    /* Adding this color for selected state */

    /* ZSList dimensions and layout */
    --zs-list-container-border: 1px solid var(--color-list-border);
    --zs-list-container-border-radius: 4px;
    --zs-list-filter-padding: 8px;
    --zs-list-filter-border-bottom: 1px solid var(--color-list-border);
    --zs-list-item-padding: 8px 12px;
    --zs-list-item-gap: 8px;
    --zs-list-item-border-bottom: 1px solid var(--color-list-item-border);
    --zs-list-empty-padding: 15px;
    --zs-list-max-height: 250px;
    /* Adding max height for dropdown lists */
}

.zs-list-container {
    /* Add container styling */
    border: var(--zs-list-container-border);
    border-radius: var(--zs-list-container-border-radius);
    overflow: hidden;
    background-color: white;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.zs-list-filter {
    padding: var(--zs-list-filter-padding);
    border-bottom: var(--zs-list-filter-border-bottom);
}

.zs-list-filter input {
    width: 100%;
    padding: 6px 10px;
    border: 1px solid var(--color-list-filter-border);
    border-radius: 3px;
    box-sizing: border-box;
    /* Include padding and border in the element's total width and height */
}

.zs-list {
    list-style: none;
    padding: 0;
    margin: 0;
    max-height: var(--zs-list-max-height, 250px);
    overflow-y: auto;
    background-color: white;
}

/* Improved scrollbar styling for ZSList */
.zs-list::-webkit-scrollbar {
    width: 6px;
}

.zs-list::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.zs-list::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
}

.zs-list::-webkit-scrollbar-thumb:hover {
    background: #aaa;
}

/* Adjust list height for different screen sizes */
@media (max-height: 600px) {
    .zs-list {
        max-height: 200px;
    }
}

@media (min-height: 800px) {
    .zs-list {
        max-height: 300px;
    }
}

.zs-list-item {
    padding: var(--zs-list-item-padding);
    cursor: default;
    /* Default cursor */
    display: flex;
    align-items: center;
    gap: var(--zs-list-item-gap);
    /* Space between checkbox and content */
    border-bottom: var(--zs-list-item-border-bottom);
}

.zs-list-item:last-child {
    border-bottom: none;
}

.zs-list-item:hover {
    background-color: var(--color-list-item-hover);
}

.zs-list-item.selected {
    background-color: var(--color-list-item-selected);
    color: var(--color-blue);
    font-weight: bold;
}

.zs-list-item.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background-color: var(--color-list-item-disabled);
    /* Optional: distinct background for disabled */
    pointer-events: none;
    /* Prevent clicks on the item itself */
}

/* Ensure pointer-events are re-enabled for interactive elements *inside* if needed,
   but generally, the whole item is non-interactive */
.zs-list-item.disabled>* {
    pointer-events: none;
    /* Disable events on children like the checkbox */
}


/* Add cursor pointer only if selectable and NOT disabled */
.zs-list-item.selectable:not(.disabled) {
    cursor: pointer;
}

/* Add cursor pointer for multiselect items that are NOT disabled */
/* Using a more compatible approach than :has selector */
.zs-list-item:not(.disabled) {
    cursor: pointer;
}


.zs-list-empty {
    padding: var(--zs-list-empty-padding);
    text-align: center;
    color: var(--color-list-empty-text);
}

/* Basic styling for checkbox */
.zs-list-item input[type="checkbox"] {
    cursor: pointer;
}

.zs-list-item.disabled input[type="checkbox"] {
    cursor: not-allowed;
}
</style>
